import { useEffect } from 'react';

export default function SEO({ 
  title, 
  description, 
  pathname = '', 
  faq = [], 
  dateModified = '2026-10-01', 
  datePublished = '2026-10-01',
  breadcrumbs = [],
  isHome = false 
}) {
  useEffect(() => {
    // Title under 60 chars check / ensure
    if (title) {
      document.title = title.length > 60 ? title.slice(0, 59) + '…' : title;
    }

    // Meta description under 160 chars check / ensure
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    const cleanDesc = description && description.length > 160 ? description.slice(0, 159) + '…' : description;
    metaDesc.setAttribute('content', cleanDesc || '');

    // Canonical link pointing to www.trailerregler.dk
    const cleanPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
    const canonicalUrl = `https://www.trailerregler.dk${cleanPath === '/' ? '' : cleanPath}`;

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // og:url must equal canonical, never current hostname
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', canonicalUrl);

    // twitter:url must equal canonical, never current hostname
    let twUrl = document.querySelector('meta[name="twitter:url"]');
    if (!twUrl) {
      twUrl = document.createElement('meta');
      twUrl.setAttribute('name', 'twitter:url');
      document.head.appendChild(twUrl);
    }
    twUrl.setAttribute('content', canonicalUrl);

    // Clean up old JSON-LD scripts created by this component
    const oldScripts = document.querySelectorAll('script[data-schema="trailerregler"]');
    oldScripts.forEach(el => el.remove());

    // WebSite schema for homepage
    if (isHome) {
      const websiteSchema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Trailerregler.dk",
        "url": "https://www.trailerregler.dk/"
      };
      const sWeb = document.createElement('script');
      sWeb.type = 'application/ld+json';
      sWeb.setAttribute('data-schema', 'trailerregler');
      sWeb.textContent = JSON.stringify(websiteSchema);
      document.head.appendChild(sWeb);
    } else {
      // Article schema (publisher = Organization "Trailerregler.dk", NO person objects, inLanguage: da)
      const articleSchema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": title,
        "description": cleanDesc,
        "inLanguage": "da",
        "datePublished": datePublished,
        "dateModified": dateModified,
        "mainEntityOfPage": canonicalUrl,
        "publisher": {
          "@type": "Organization",
          "name": "Trailerregler.dk",
          "url": "https://www.trailerregler.dk"
        }
      };

      const s1 = document.createElement('script');
      s1.type = 'application/ld+json';
      s1.setAttribute('data-schema', 'trailerregler');
      s1.textContent = JSON.stringify(articleSchema);
      document.head.appendChild(s1);
    }

    // BreadcrumbList schema if breadcrumbs present
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, idx) => ({
          "@type": "ListItem",
          "position": idx + 1,
          "name": crumb.name,
          "item": crumb.path.startsWith('http') 
            ? crumb.path 
            : `https://www.trailerregler.dk${crumb.path === '/' ? '/' : (crumb.path.startsWith('/') ? crumb.path : '/' + crumb.path)}`
        }))
      };

      const sBc = document.createElement('script');
      sBc.type = 'application/ld+json';
      sBc.setAttribute('data-schema', 'trailerregler');
      sBc.textContent = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(sBc);
    }

    // FAQ schema if faq items present
    if (faq && faq.length > 0) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faq.map(item => ({
          "@type": "Question",
          "name": item.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.a
          }
        }))
      };

      const s2 = document.createElement('script');
      s2.type = 'application/ld+json';
      s2.setAttribute('data-schema', 'trailerregler');
      s2.textContent = JSON.stringify(faqSchema);
      document.head.appendChild(s2);
    }

    return () => {
      const cleanupScripts = document.querySelectorAll('script[data-schema="trailerregler"]');
      cleanupScripts.forEach(el => el.remove());
    };
  }, [title, description, pathname, faq, dateModified, datePublished, breadcrumbs, isHome]);

  return null;
}
